import { Button } from "@/components/ui/button";
import { getCategoryDisplay, getFrequencyDisplay } from "../../util";

import { Body } from "@hypercard/web-components";
import { Card } from "@/components/ui/card";
import { CreateStipendStep } from "./types";
import { AnimatedProps, animated } from "@react-spring/web";
import { CSSProperties, useState } from "react";
import {
  DepartmentOption,
  entireCompany,
} from "@common/components/SelectDepartment";
import { StipendCategory, StipendRuleFrequency } from "@common/utils/graphql";
import { Separator } from "@/components/ui/separator";
import { formatDollars } from "@common/components/utils";
import { getDepartmentDisplay } from "../../util";
import { useSetInitialCreateOrEditStipendState } from "@common/state/createOrEditStipendState";

type Template = {
  name: string;
  longDetails: string;
  amount: number;
  departments: DepartmentOption[];
  frequency: StipendRuleFrequency;
  color: string;
  category: StipendCategory;
  terms: string;
};

const templates: Template[] = [
  {
    name: "Daily Lunch Stipend",
    longDetails:
      "Enjoy a daily lunch on us! Your expenses will be auto-approved for any restaurant or food delivery service.",
    amount: 15,
    departments: [entireCompany],
    frequency: StipendRuleFrequency.Daily,
    color: "#C8ACF0",
    category: StipendCategory.Food,
    terms: "A receipt is required for all transactions.",
  },
  {
    name: "Home Office Stipend",
    longDetails:
      "Utilize this stipend to equip your home office with essential tools and equipment, ensuring a comfortable and productive remote work environment.",
    amount: 250,
    departments: [entireCompany],
    frequency: StipendRuleFrequency.OneTime,
    color: "#F2566D",
    category: StipendCategory.Convenience,
    terms:
      "A receipt is required for all transactions. Any transaction over $100 requires approval.",
  },
  {
    name: "Wellness Stipend",
    longDetails:
      "Apply this stipend towards activities and services that promote your physical and mental well-being, helping you maintain a healthy work-life balance.",
    amount: 200,
    departments: [entireCompany],
    frequency: StipendRuleFrequency.Monthly,
    color: "#F7D674",
    category: StipendCategory.Wellness,
    terms:
      "A receipt is required for all transactions. Any transaction over $100 requires approval.",
  },
  {
    name: "Professional Development Stipend",
    longDetails:
      "Invest in your career growth with this stipend, which can be used for courses, workshops, or certifications that enhance your professional skills.",
    amount: 15,
    departments: [entireCompany],
    frequency: StipendRuleFrequency.Daily,
    color: "#54C186",
    category: StipendCategory.Education,
    terms: "A receipt is required for all transactions.",
  },
  {
    name: "Health & Fitness Stipend",
    longDetails:
      "Use this stipend to support your physical health through gym memberships, fitness classes, or sports equipment that keeps you active and energized.",
    amount: 15,
    departments: [entireCompany],
    frequency: StipendRuleFrequency.Daily,
    color: "#65DCFF",
    category: StipendCategory.Fitness,
    terms: "A receipt is required for all transactions.",
  },
  {
    name: "Transportation Stipend",
    longDetails:
      "Cover your daily commute or transportation needs with this stipend, designed to ease the cost of travel, whether by public transit, rideshare, or personal vehicle",
    amount: 15,
    departments: [entireCompany],
    frequency: StipendRuleFrequency.Daily,
    color: "#5854C1",
    category: StipendCategory.Commute,
    terms:
      "A receipt is required for all transactions, except for public transportation.",
  },
];

type Props = AnimatedProps<{
  onNext: (step: CreateStipendStep) => void;
  style: CSSProperties;
  onBack: () => void;
}>;

export const SelectTemplate = ({ onNext, onBack, style }: Props) => {
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );
  const setInitialStipendState = useSetInitialCreateOrEditStipendState();

  const handleNext = () => {
    if (selectedTemplate) {
      setInitialStipendState(selectedTemplate);
      onNext(CreateStipendStep.BaseForm);
    }
  };
  return (
    <animated.div
      style={{ opacity: style.opacity }}
      className="flex w-full grow flex-col items-center justify-center pl-6 lg:pl-14"
    >
      <Body size="2xl" weight="medium">
        Select a Template
      </Body>
      <Body
        size="md"
        weight="medium"
        className="mt-3 text-center text-white/70"
      >
        These templates were made based on typical company stipends
      </Body>
      <div className="mx-auto mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {templates.map((template) => (
          <button
            key={`stipend-templates--template-${template.name}`}
            onClick={() => {
              setSelectedTemplate(template);
            }}
            role="radio"
            aria-label={template.name}
            aria-checked={selectedTemplate?.name === template.name}
            className="group relative z-10 flex max-w-[350px] overflow-hidden rounded-md bg-border p-px text-start aria-checked:bg-white/50"
          >
            <div
              className="absolute -left-24 -top-40 -z-20 size-64 rounded-full blur-3xl"
              style={{
                backgroundColor: template.color,
              }}
            />
            <Card className="flex h-full grow flex-col items-start border-none p-6 group-aria-checked:bg-[#1B1B1B]">
              <Body size="lg" weight="medium">
                {template.name}
              </Body>
              <Body size="md" weight="regular" className="mt-2 text-white/70">
                {template.longDetails}
              </Body>
              <Separator orientation="horizontal" className="my-4 w-full" />
              <div className="flex w-full">
                <div className="grow space-y-3">
                  <Body size="sm" weight="regular">
                    Amount
                  </Body>
                  <Body size="md" weight="regular">
                    {formatDollars(template.amount)}
                  </Body>
                </div>
                <div className="grow space-y-3">
                  <Body size="sm" weight="regular">
                    Category
                  </Body>
                  <Body size="md" weight="regular">
                    {getCategoryDisplay(template.category)}
                  </Body>
                </div>
                <div className="grow space-y-3">
                  <Body size="sm" weight="regular">
                    Department
                  </Body>
                  <Body size="md" weight="regular">
                    {getDepartmentDisplay(template.departments!)}
                  </Body>
                </div>
              </div>
              <div className="mt-8 grow space-y-3">
                <Body size="sm" weight="regular">
                  Frequency
                </Body>
                <div className="rounded bg-[#1B1B1B] px-4 py-2 group-aria-checked:bg-[#272727]">
                  <Body size="sm" weight="medium" className="text-white/70">
                    {getFrequencyDisplay(template.frequency)}
                  </Body>
                </div>
              </div>
            </Card>
          </button>
        ))}
        <div className="col-span-full mt-8 flex w-full flex-grow items-end justify-between">
          <Button type="button" variant="ghost" onClick={onBack}>
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={!selectedTemplate}
            type="submit"
            className="w-44"
          >
            Next
          </Button>
        </div>
      </div>
    </animated.div>
  );
};
